import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  FormControlLabel,
  Chip,
  Alert,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Divider,
  Tabs,
  Tab,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  Security as SecurityIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  People as PeopleIcon,
  Group as GroupIcon
} from '@mui/icons-material';
import { api } from '../contexts/ApiContext';
import { useAuth } from '../contexts/AuthContext';
import { EntityPermissions } from '../components/EntityPermissions';
import { PagesPermissions } from '../components/PagesPermissions';
import { useNavigate, useLocation } from 'react-router-dom';
import { Storage as StorageIcon, Article as PagesIcon } from '@mui/icons-material';

interface RBACManagementProps {
  defaultTab?: number;
}

interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  role: 'owner' | 'admin' | 'user' | 'guest';
  is_active: boolean;
  last_login: string | null;
  created_at: string;
  updated_at: string;
  // RBAC Permissions
  can_view_properties: boolean;
  can_view_balances: boolean;
  can_view_calendar: boolean;
  can_view_entities: boolean;
  can_manage_families: boolean;
}

interface PermissionGroup {
  title: string;
  description: string;
  permissions: {
    key: keyof User;
    label: string;
    description: string;
  }[];
}

const PERMISSION_GROUPS: PermissionGroup[] = [
  {
    title: 'Financial Data',
    description: 'Access to financial information and balances',
    permissions: [
      {
        key: 'can_view_balances',
        label: 'View Balances',
        description: 'Access to bank account balances and financial summaries'
      }
    ]
  },
  {
    title: 'Property Management',
    description: 'Access to property-related information',
    permissions: [
      {
        key: 'can_view_properties',
        label: 'View Properties',
        description: 'Access to property listings, valuations, and details'
      }
    ]
  },
  {
    title: 'Calendar & Bookings',
    description: 'Access to booking and calendar information',
    permissions: [
      {
        key: 'can_view_calendar',
        label: 'View Calendar',
        description: 'Access to booking calendar and reservation data'
      }
    ]
  },
  {
    title: 'Entity Management',
    description: 'Access to business entities and family management',
    permissions: [
      {
        key: 'can_view_entities',
        label: 'View Entities',
        description: 'Access to companies, loans, and other business entities'
      },
      {
        key: 'can_manage_families',
        label: 'Manage Families',
        description: 'Create, edit, and manage family member information'
      }
    ]
  }
];

// TabPanel component for Material-UI tabs
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`rbac-tabpanel-${index}`}
      aria-labelledby={`rbac-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `rbac-tab-${index}`,
    'aria-controls': `rbac-tabpanel-${index}`,
  };
}

export const RBACManagement: React.FC<RBACManagementProps> = ({ defaultTab = 0 }) => {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tabValue, setTabValue] = useState(defaultTab);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/admin/users');
      setUsers(response.data.users);
    } catch (err: any) {
      setError('Failed to fetch users');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Sync tab value with URL
  useEffect(() => {
    const path = location.pathname;
    if (path === '/rbac/pages') {
      setTabValue(1);
    } else if (path === '/rbac' || path === '/rbac/entities') {
      setTabValue(0); // Default to Entities tab
    }
  }, [location.pathname]);

  const handlePermissionChange = async (userId: number, permission: keyof User, value: boolean) => {
    try {
      setSaving(true);
      setError('');
      
      await api.put(`/api/admin/users/${userId}/permissions`, {
        [permission]: value
      });
      
      // Update local state
      setUsers(prev => prev.map(user => 
        user.id === userId 
          ? { ...user, [permission]: value }
          : user
      ));
      
      setSuccess('Permission updated successfully');
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (err: any) {
      setError('Failed to update permission');
      console.error('Error updating permission:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleRoleChange = async (userId: number, newRole: string) => {
    try {
      setSaving(true);
      setError('');
      
      await api.put(`/api/admin/users/${userId}/role`, {
        role: newRole
      });
      
      // Update local state
      setUsers(prev => prev.map(user => 
        user.id === userId 
          ? { ...user, role: newRole as 'owner' | 'admin' | 'user' | 'guest' }
          : user
      ));
      
      setSuccess('Role updated successfully');
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (err: any) {
      setError('Failed to update role');
      console.error('Error updating role:', err);
    } finally {
      setSaving(false);
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'owner': return 'error';
      case 'admin': return 'error';
      case 'user': return 'primary';
      case 'guest': return 'default';
      default: return 'default';
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    
    // Navigate to the appropriate URL based on tab selection
    switch (newValue) {
      case 0:
        navigate('/rbac/entities');
        break;
      case 1:
        navigate('/rbac/pages');
        break;
      default:
        navigate('/rbac');
        break;
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <SecurityIcon sx={{ mr: 2, fontSize: 32 }} />
          <Box>
            <Typography variant="h4" component="h1">
              RBAC Management
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Manage permissions for entities and pages
            </Typography>
          </Box>
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="RBAC tabs">
            <Tab 
              icon={<StorageIcon />} 
              label="Entities" 
              iconPosition="start"
              {...a11yProps(0)} 
            />
            <Tab 
              icon={<PagesIcon />} 
              label="Pages" 
              iconPosition="start"
              {...a11yProps(1)} 
            />
          </Tabs>
        </Box>

        {/* Tab Panels */}
        <TabPanel value={tabValue} index={0}>
          <EntityPermissions />
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          <PagesPermissions />
        </TabPanel>
      </Box>
    </Container>
  );
};

export default RBACManagement;
